import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Modal } from 'react-bootstrap';
import { useSendDonorEmail } from 'hooks/useStripePayments';

const TEMPLATES = [
  {
    value: 'check_funds',
    label: 'Check your account for funds',
    hint: 'A payment did not go through - asks the donor to check their bank and let us know when to retry.'
  },
  {
    value: 'card_expiring',
    label: 'Your payment card is expiring',
    hint: 'Asks the donor to get in touch so we can take new card details before the next payment fails.'
  }
];

/**
 * Pre-made donor emails (#20). Sent from the server with the Help Yateem
 * letterhead/footer; a copy is logged on the donor's Notes tab.
 */
const SendDonorEmailModal = ({ show, onHide, donor }) => {
  const [template, setTemplate] = useState('check_funds');
  const [amount, setAmount] = useState('');
  const [billing, setBilling] = useState('monthly');
  const { sendDonorEmail, isSending } = useSendDonorEmail();

  const send = () =>
    sendDonorEmail(
      { donorId: donor.id, template, amount: amount || null, billing },
      { onSuccess: () => onHide() }
    );

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Send email to {donor?.first_name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!donor?.primary_email ? (
          <p className="text-danger mb-0">
            This donor has no email address on file.
          </p>
        ) : (
          <>
            <p className="text-600 fs--1">
              To <strong>{donor.primary_email}</strong>, signed with your name.
            </p>
            {TEMPLATES.map(t => (
              <Form.Check
                key={t.value}
                type="radio"
                id={`tpl-${t.value}`}
                name="template"
                className="mb-2"
                checked={template === t.value}
                onChange={() => setTemplate(t.value)}
                label={
                  <>
                    <span className="fw-medium">{t.label}</span>
                    <div className="text-600 fs--1">{t.hint}</div>
                  </>
                }
              />
            ))}
            <Form.Group className="mt-3">
              <Form.Label>
                Donation amount mentioned in the email (optional)
              </Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 29.00"
                value={amount}
                onChange={e => setAmount(e.target.value)}
              />
            </Form.Group>
            {template === 'card_expiring' && (
              <Form.Group className="mt-3">
                <Form.Label>Donation type</Form.Label>
                <Form.Select
                  value={billing}
                  onChange={e => setBilling(e.target.value)}
                >
                  <option value="monthly">Monthly</option>
                  <option value="one_off">One-off</option>
                </Form.Select>
              </Form.Group>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={isSending || !donor?.primary_email}
          onClick={send}
        >
          {isSending ? 'Sending…' : 'Send email'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

SendDonorEmailModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  donor: PropTypes.object
};

export default SendDonorEmailModal;
